package models

import kotlinx.coroutines.flow.Flow

class GameRepository(
    private val remoteDataSource: GameDataSource,
    private val localDataSource: GameDataSource
) : GameDataSource {

    private var useLocalDataSource: Boolean = false

    fun setUseLocalDataSource(useLocal: Boolean) {
        useLocalDataSource = useLocal
    }

    private val currentDataSource: GameDataSource
        get() = if (useLocalDataSource) localDataSource else remoteDataSource

    // The LOCAL sentinel code always routes to the local data source, regardless of the mode
    // toggle: a single-device game must keep working even if connectivity changes flip the
    // repository back to online mid-game (and the practice game launches without touching
    // the toggle at all).
    private fun dataSourceFor(gameCode: String): GameDataSource =
        if (gameCode == LOCAL_GAME_CODE) localDataSource else currentDataSource

    override suspend fun deleteGame(gameCode: String) =
        dataSourceFor(gameCode).deleteGame(gameCode)

    override suspend fun finishGame(gameCode: String) =
        dataSourceFor(gameCode).finishGame(gameCode)

    override suspend fun updateSetting(gameCode: String, game: DomainGame, settingToUpdate: Setting) =
        dataSourceFor(gameCode).updateSetting(gameCode, game, settingToUpdate)

    override suspend fun makeHost(gameCode: String, playerName: String) =
        dataSourceFor(gameCode).makeHost(gameCode, playerName)

    override suspend fun removePlayer(gameCode: String, playerName: String) =
        dataSourceFor(gameCode).removePlayer(gameCode, playerName)

    override suspend fun bankPoints(
        gameCode: String,
        bankingPlayers: List<String>,
        nextRoundModifier: RoundModifier,
    ) = dataSourceFor(gameCode).bankPoints(
        gameCode,
        bankingPlayers,
        nextRoundModifier,
    )

    override suspend fun rollDice(
        gameCode: String,
        expectedRound: Round?,
        round: Round,
        nextPlayerName: String,
        rolledValue: Int,
    ) = dataSourceFor(gameCode).rollDice(
        gameCode,
        expectedRound,
        round,
        nextPlayerName,
        rolledValue,
    )

    override suspend fun startGame(
        gameCode: String,
        activeOrderedPlayerNames: List<String>,
        firstRoundModifier: RoundModifier,
    ) = dataSourceFor(gameCode).startGame(gameCode, activeOrderedPlayerNames, firstRoundModifier)

    override suspend fun createGame(gameCode: String, name: String, hostPerksActive: Boolean, diceSkin: String, diceHat: String): String =
        dataSourceFor(gameCode).createGame(gameCode, name, hostPerksActive, diceSkin, diceHat)

    override suspend fun rematch(gameCode: String, hostPerksActive: Boolean) =
        dataSourceFor(gameCode).rematch(gameCode, hostPerksActive)

    override suspend fun joinGame(player: Player, gameCode: String): JoinGameResult =
        dataSourceFor(gameCode).joinGame(player, gameCode)

    override suspend fun setGamePublic(gameCode: String, isPublic: Boolean) =
        dataSourceFor(gameCode).setGamePublic(gameCode, isPublic)

    override fun publicGamesFlow(): Flow<List<PublicGameSummary>> =
        remoteDataSource.publicGamesFlow()

    override fun firebaseGameFlow(gameCode: String): Flow<FirebaseGame?> =
        dataSourceFor(gameCode).firebaseGameFlow(gameCode)

    override fun firebasePlayersFlow(gameCode: String): Flow<List<Player>> =
        dataSourceFor(gameCode).firebasePlayersFlow(gameCode)

    override fun tableReactionsFlow(gameCode: String): Flow<List<TableReaction>> =
        dataSourceFor(gameCode).tableReactionsFlow(gameCode)

    override fun domainGameFlow(gameCode: String): Flow<DomainGame?> =
        dataSourceFor(gameCode).domainGameFlow(gameCode)

    override suspend fun gameExists(gameCode: String): Boolean =
        dataSourceFor(gameCode).gameExists(gameCode)

    override suspend fun updatePlayerOrder(gameCode: String, playerNames: List<String>) =
        dataSourceFor(gameCode).updatePlayerOrder(gameCode, playerNames)

    override suspend fun updateEndRoundNum(gameCode: String, endRoundNum: Int) =
        dataSourceFor(gameCode).updateEndRoundNum(gameCode, endRoundNum)

    override suspend fun addPlayer(player: Player, gameCode: String) =
        dataSourceFor(gameCode).addPlayer(player, gameCode)

    override suspend fun sendNudge(gameCode: String, nudge: Nudge) =
        dataSourceFor(gameCode).sendNudge(gameCode, nudge)

    override suspend fun sendTableReaction(gameCode: String, reaction: TableReaction) =
        dataSourceFor(gameCode).sendTableReaction(gameCode, reaction)

    companion object {
        /** Game code for on-device games (single-device play and the first-launch practice game). */
        const val LOCAL_GAME_CODE = "LOCAL"
    }
}
